import { APPEAL_STATUSES, PRIORITIES, SUBMISSION_STATUSES } from '@kinohelpdesk/shared';
import { AlertCircle, CheckCircle, Circle, Clock, type LucideIcon, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_ICONS: Record<string, LucideIcon> = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  cancelled: Circle,
  low: Circle,
  medium: AlertCircle,
  high: AlertCircle,
  critical: AlertCircle,
};

interface StatusBadgeProps {
  status: string;
  type?: 'submission' | 'appeal' | 'priority';
  className?: string;
}

export function StatusBadge({ status, type = 'submission', className }: StatusBadgeProps) {
  let config: { label: string; emoji: string; color?: string } | undefined;

  if (type === 'priority') {
    config = PRIORITIES[status as keyof typeof PRIORITIES];
  } else if (type === 'appeal') {
    config = APPEAL_STATUSES[status as keyof typeof APPEAL_STATUSES];
  } else {
    config = SUBMISSION_STATUSES[status as keyof typeof SUBMISSION_STATUSES];
  }

  if (!config) {
    return (
      <span
        className={cn('px-2 py-1 text-xs rounded-full bg-gray-500/10 text-gray-400', className)}
      >
        {status}
      </span>
    );
  }

  const statusClasses: Record<string, string> = {
    pending: 'status-pending',
    approved: 'status-approved',
    rejected: 'status-rejected',
    cancelled: 'status-cancelled',
    low: 'bg-blue-500/10 text-blue-400 border border-blue-500/30',
    medium: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30',
    high: 'bg-orange-500/10 text-orange-400 border border-orange-500/30',
    critical: 'bg-red-500/10 text-red-400 border border-red-500/30',
  };

  const Icon = STATUS_ICONS[status] ?? Circle;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full',
        statusClasses[status] ?? 'bg-gray-500/10 text-gray-400',
        className,
      )}
    >
      <Icon className="w-3 h-3" />
      <span>{config.label}</span>
    </span>
  );
}
